import { httpClient } from "@/core/api/client/httpClient";
import { isHttpError  } from "@/core/api/errors/HttpError";

import type {
    AdvisorResponse,
    AnswerPerfilamientoRequest,
    AnswerPerfilamientoResponse,
    BffApiResponse,
    CompletePerfilamientoResponse,
    FilterPerfilamientoRequest,
    FilterPerfilamientoResponse,
    PerfilProcesoResponse,
    PerfilStatusResponse,
    StartPerfilamientoRequest,
    StartPerfilamientoResponse
} from './perfilamiento.types'

export function normalizePerfilamientoLang(lang?: string | null) {
    return lang?.toLowerCase().startsWith('en') ? 'en' : 'es'
}


export type PerfilamientoResult<T> = 
| { success : true; data: T }
| { success : false; errorCode: string ; message : string }

type MediaResourceResponse = {
    name?: string | null
    url?: string | null
    contentType?: string | null
}

// El modal genérico solo corresponde a fallos de infraestructura/backend.
// Los códigos de negocio deben permanecer en el flujo para que la pantalla
// pueda decidir cómo continuar (por ejemplo, mostrar desempate o asesoría).
export function isTechnicalPerfilamientoError(errorCode: string) {
    return ['INTERNAL_ERROR', 'NETWORK_ERROR', 'PERFILAMIENTO_BACKEND_ERROR'].includes(errorCode)
}

type PerfilamientoErrorBody = Partial<BffApiResponse<unknown>> & {
    message?: string | null
}

function toResult<T>(response : BffApiResponse<T> ) : PerfilamientoResult<T> {
    if (response.success && response.data !== null && response.data !== undefined ) {
        return { success:true , data : response.data }
}
    return {
        success: false,
        errorCode : response.error?.code ?? 'INTERNAL_ERROR',
        message : response.error?.message ?? 'Error inesperado'
    }
}

function toErrorResult<T>(error: unknown) : PerfilamientoResult<T>{
    if(isHttpError(error) && error.data){
        const body = error.data as PerfilamientoErrorBody
        const message = body.error?.message ?? body.message

        return {
            success: false,
            errorCode: body.error?.code ?? (error.status === 409 ? 'CONFLICT' : 'INTERNAL_ERROR'),
            message: message ?? 'Error inesperado',
        }
    }

    return {
        success : false,
        errorCode: 'NETWORK_ERROR',
        message : 'Sin Conexion'
    }
}

export const perfilamientoService ={
    async getAvatarResource(avatar: string, canal = 'movil'): Promise<PerfilamientoResult<MediaResourceResponse>> {
        try {
            const filename = avatar.split('?')[0].split('/').pop()

            if (!filename) {
                return {
                    success: false,
                    errorCode: 'RESOURCE_NOT_FOUND',
                    message: 'No se encontró el recurso multimedia.',
                }
            }

            const data = await httpClient.get<BffApiResponse<MediaResourceResponse>>(
                `/api/media/${encodeURIComponent(filename)}?canal=${encodeURIComponent(canal)}`,
            )
            return toResult(data)
        } catch (error) {
            return toErrorResult(error)
        }
    },

    async getStatus (lang = 'es') : Promise<PerfilamientoResult<PerfilStatusResponse>>{
        const normalizedLang = normalizePerfilamientoLang(lang)

        try {
            const data = await httpClient.get<BffApiResponse<PerfilStatusResponse>>(
                `/api/perfilamiento/status?lang=${normalizedLang}`
            )
            return toResult(data)
        }catch(error){
            return toErrorResult(error)
        }
    },

    async start(
        payload: StartPerfilamientoRequest,
        lang = 'es',        
    ) : Promise<PerfilamientoResult<StartPerfilamientoResponse>>{
        const normalizedLang = normalizePerfilamientoLang(lang)

        try {
            const data = await httpClient.post<BffApiResponse<StartPerfilamientoResponse>>(
                 `/api/perfilamiento/start?lang=${normalizedLang}`, payload,
            )
            return toResult(data)
        }catch(error){
            console.log(error);
            return toErrorResult(error)             
        }
    },

    async filter(
      processId: string,
      payload: FilterPerfilamientoRequest,
      lang = 'es',
    ): Promise<PerfilamientoResult<FilterPerfilamientoResponse>> {
      const normalizedLang = normalizePerfilamientoLang(lang)

      try {
        const data = await httpClient.post<BffApiResponse<FilterPerfilamientoResponse>>(
          `/api/perfilamiento/${processId}/filter?lang=${normalizedLang}`,
          payload,
        )
        return toResult(data)
      } catch (error) {
        return toErrorResult(error)
      }
    },

    async getProceso(
      processId: string,
      lang = 'es',
    ): Promise<PerfilamientoResult<PerfilProcesoResponse>> {
      const normalizedLang = normalizePerfilamientoLang(lang)

      try {
        const data = await httpClient.get<BffApiResponse<PerfilProcesoResponse>>(
          `/api/perfilamiento/${processId}?lang=${normalizedLang}`,
        )
        return toResult(data)
      } catch (error) {
        return toErrorResult(error)
      }
    },

    async answer(
      processId: string,
      payload: AnswerPerfilamientoRequest,
      lang = 'es',
    ): Promise<PerfilamientoResult<AnswerPerfilamientoResponse>> {
      const normalizedLang = normalizePerfilamientoLang(lang)

      try {
        const data = await httpClient.post<BffApiResponse<AnswerPerfilamientoResponse>>(
          `/api/perfilamiento/${processId}/answer?lang=${normalizedLang}`,
          payload,
        )
        return toResult(data)
      } catch (error) {
        return toErrorResult(error)
      }
    },

    async complete(
      processId: string,
      lang = 'es',
    ): Promise<PerfilamientoResult<CompletePerfilamientoResponse>> {
      const normalizedLang = normalizePerfilamientoLang(lang)

      try {
        const data = await httpClient.post<BffApiResponse<CompletePerfilamientoResponse>>(
          `/api/perfilamiento/${processId}/complete?lang=${normalizedLang}`,
          {},
        )
        return toResult(data)
      } catch (error) {
        return toErrorResult(error)
      }
    },

    async advisory(
      processId: string,
      lang = 'es',
    ): Promise<PerfilamientoResult<AdvisorResponse>> {
      const normalizedLang = normalizePerfilamientoLang(lang)

      try {
        const data = await httpClient.post<BffApiResponse<AdvisorResponse>>(
          `/api/perfilamiento/${processId}/advisory?lang=${normalizedLang}`,
          {},
        )
        return toResult(data)
      } catch (error) {
        return toErrorResult(error)
      }
    },
}
